import { useEffect, type ReactNode } from "react";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ARTICLES } from "@/data/articlesData";
import ProjectGallery from "@/components/ProjectGallery";
import {
  ArrowRight,
  Calendar,
  User,
  Clock,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = ARTICLES.find((a) => a.slug === slug);

  const seoTitle = article
    ? article.seoTitle || `${article.title} | Abacus Insights`
    : "Article Not Found | Abacus Insights";
  const seoDescription = article
    ? article.seoDescription || article.excerpt
    : "The article you are looking for may have been moved or is no longer available.";
  const seoCanonical = article ? `/insights/${article.slug}` : "/insights";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex flex-col font-body">
        <Navbar forceSolid />
        <div className="flex-1 flex items-center justify-center pt-32 pb-20">
          <div className="text-center max-w-md px-5">
            <h1 className="font-heading text-3xl font-extrabold text-[#002b5c] uppercase mb-4">
              Article Not Found
            </h1>
            <p className="text-gray-500 mb-8">
              The article you are looking for may have been moved or is no
              longer available.
            </p>
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 bg-[#002b5c] text-white px-6 py-3 rounded-xs font-heading font-bold text-xs uppercase tracking-wider hover:bg-[#ffcc29] hover:text-[#002b5c] transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentIndex = ARTICLES.findIndex((a) => a.slug === slug);
  const prevArticle = currentIndex > 0 ? ARTICLES[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < ARTICLES.length - 1 ? ARTICLES[currentIndex + 1] : null;
  const relatedArticles = ARTICLES.filter(
    (a) => a.slug !== slug && a.category === article.category,
  ).slice(0, 3);
  const fallbackRelated = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const related =
    relatedArticles.length >= 2 ? relatedArticles : fallbackRelated;

  return (
    <div className="min-h-screen bg-background flex flex-col font-body">
      <Navbar />

      {/* Breadcrumbs */}
      <div className="pt-28 pb-4 bg-[#002b5c]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-12">
          <nav className="flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-widest text-white/60">
            <Link to="/" className="hover:text-[#ffcc29] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link
              to="/insights"
              className="hover:text-[#ffcc29] transition-colors"
            >
              Insights
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#ffcc29] truncate max-w-[200px]">
              {article.category}
            </span>
          </nav>
        </div>
      </div>

      {/* Article Hero */}
      <section className="relative bg-[#002b5c] text-white overflow-hidden pb-16">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffcc29_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-12 relative z-10">
          <span className="text-[#ffcc29] font-heading font-bold text-sm tracking-[0.2em] uppercase block mb-4">
            {article.category}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tight mb-6 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-white/60">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4 text-[#ffcc29]" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-[#ffcc29]" />
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[#ffcc29]" />
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Hero Image + Gallery Thumbnails */}
      <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-12 -mt-8 relative z-20">
        {article.gallery && article.gallery.length > 0 ? (
          <ProjectGallery
            images={[article.image, ...article.gallery]}
            imageAlt={article.imageAlt || article.title}
          />
        ) : (
          <div className="aspect-[16/9] overflow-hidden rounded-xs shadow-2xl bg-[#002b5c]">
            <img
              src={article.image}
              alt={article.imageAlt || article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Article Body */}
      <article className="py-12 sm:py-16 flex-1">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-12">
          {/* Excerpt */}
          <p className="text-gray-600 text-lg sm:text-xl leading-relaxed mb-10 font-body border-l-4 border-[#ffcc29] pl-6">
            {article.excerpt}
          </p>

          {/* Content */}
          <div className="space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed">
            {(() => {
              const renderInline = (text: string): ReactNode[] => {
                const nodes: ReactNode[] = [];
                let rk = 0;
                const re = /(\*\*([^*]+?)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
                let lastIdx = 0;
                let m: RegExpExecArray | null;
                while ((m = re.exec(text)) !== null) {
                  if (m.index > lastIdx) {
                    nodes.push(text.slice(lastIdx, m.index));
                  }
                  if (m[1]) {
                    nodes.push(
                      <strong
                        key={`b${rk++}`}
                        className="font-semibold text-[#002b5c]"
                      >
                        {m[2]}
                      </strong>,
                    );
                  } else if (m[3]) {
                    const label = m[4];
                    const href = m[5];
                    if (href.startsWith("/")) {
                      nodes.push(
                        <Link
                          key={`l${rk++}`}
                          to={href}
                          className="text-[#002b5c] font-semibold underline decoration-[#ffcc29] underline-offset-2 hover:text-[#ffcc29] transition-colors"
                        >
                          {label}
                        </Link>,
                      );
                    } else {
                      nodes.push(
                        <a
                          key={`l${rk++}`}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#002b5c] font-semibold underline decoration-[#ffcc29] underline-offset-2 hover:text-[#ffcc29] transition-colors"
                        >
                          {label}
                        </a>,
                      );
                    }
                  }
                  lastIdx = re.lastIndex;
                }
                if (lastIdx < text.length) {
                  nodes.push(text.slice(lastIdx));
                }
                return nodes;
              };

              const parseTableRow = (row: string) => {
                const cells = row.split("|");
                if (cells[0]?.trim() === "") cells.shift();
                if (cells[cells.length - 1]?.trim() === "") cells.pop();
                return cells.map((c) => c.trim());
              };

              const blocks: ReactNode[] = [];
              let i = 0;
              let key = 0;

              while (i < article.content.length) {
                const paragraph = article.content[i];

                // Tab-separated table (legacy format)
                if (paragraph.includes("\t")) {
                  const rows: string[] = [];
                  while (
                    i < article.content.length &&
                    article.content[i].includes("\t")
                  ) {
                    rows.push(article.content[i]);
                    i++;
                  }
                  const [header, ...body] = rows.map((r) => r.split("\t"));
                  blocks.push(
                    <div key={key++} className="overflow-x-auto my-8">
                      <table className="w-full text-left text-sm sm:text-base border-collapse">
                        <thead>
                          <tr className="bg-[#002b5c] text-white">
                            {header.map((cell, ci) => (
                              <th
                                key={ci}
                                className="px-3 py-2.5 font-heading font-bold uppercase tracking-wider text-xs"
                              >
                                {cell}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {body.map((row, ri) => (
                            <tr
                              key={ri}
                              className={ri % 2 ? "bg-[#002b5c]/5" : "bg-white"}
                            >
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className="px-3 py-2.5 border-t border-gray-100 align-top"
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>,
                  );
                  continue;
                }

                // Markdown pipe table
                if (paragraph.startsWith("|")) {
                  const rows: string[] = [];
                  while (
                    i < article.content.length &&
                    article.content[i].startsWith("|")
                  ) {
                    rows.push(article.content[i]);
                    i++;
                  }
                  const header = parseTableRow(rows[0]);
                  const bodyRows = rows
                    .slice(1)
                    .filter((r) => r.replace(/[\|:\s-]/g, "") !== "");
                  blocks.push(
                    <div key={key++} className="overflow-x-auto my-8">
                      <table className="w-full text-left text-sm sm:text-base border-collapse">
                        <thead>
                          <tr className="bg-[#002b5c] text-white">
                            {header.map((cell, ci) => (
                              <th
                                key={ci}
                                className="px-3 py-2.5 font-heading font-bold uppercase tracking-wider text-xs"
                              >
                                {renderInline(cell)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bodyRows.map((row, ri) => {
                            const cells = parseTableRow(row);
                            return (
                              <tr
                                key={ri}
                                className={
                                  ri % 2 ? "bg-[#002b5c]/5" : "bg-white"
                                }
                              >
                                {cells.map((cell, ci) => (
                                  <td
                                    key={ci}
                                    className="px-3 py-2.5 border-t border-gray-100 align-top"
                                  >
                                    {renderInline(cell)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>,
                  );
                  continue;
                }

                const isH3 = paragraph.startsWith("### ");
                const isH2Hash = paragraph.startsWith("## ");
                const isCustomHeading = paragraph.startsWith("# ");
                const isNumberedHeading = /^\d+\.\s/.test(paragraph);
                const isFaqHeading =
                  paragraph === "Quick FAQ" ||
                  paragraph === "FAQ:" ||
                  paragraph === "Frequently Asked Questions" ||
                  paragraph === "Comparison Summary:";
                const isHeading =
                  isFaqHeading ||
                  isH3 ||
                  isH2Hash ||
                  isCustomHeading ||
                  (isNumberedHeading && !article.inlineNumbers);
                const isBulletDot = paragraph.startsWith("• ");
                const isBulletDash = paragraph.startsWith("- ");

                if (isH3) {
                  blocks.push(
                    <h3
                      key={key++}
                      className="font-heading text-lg sm:text-xl font-bold text-[#002b5c] tracking-tight pt-3"
                    >
                      {renderInline(paragraph.slice(4))}
                    </h3>,
                  );
                } else if (isHeading) {
                  const headingText = isH2Hash
                    ? paragraph.slice(3)
                    : isCustomHeading
                      ? paragraph.slice(2)
                      : paragraph;
                  blocks.push(
                    <h2
                      key={key++}
                      className="font-heading text-xl sm:text-2xl font-bold text-[#002b5c] uppercase tracking-tight pt-6"
                    >
                      {renderInline(headingText)}
                    </h2>,
                  );
                } else if (isBulletDash) {
                  blocks.push(
                    <p
                      key={key++}
                      className="pl-5 text-gray-600 text-sm sm:text-base"
                    >
                      <span className="text-[#ffcc29] mr-2 font-bold">•</span>
                      {renderInline(paragraph.slice(2))}
                    </p>,
                  );
                } else if (isBulletDot) {
                  blocks.push(
                    <p
                      key={key++}
                      className="pl-4 text-gray-600 text-sm sm:text-base"
                    >
                      {renderInline(paragraph)}
                    </p>,
                  );
                } else {
                  blocks.push(<p key={key++}>{renderInline(paragraph)}</p>);
                }
                i++;
              }
              return blocks;
            })()}
          </div>

          {/* Share / CTA */}

          <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm text-gray-500 font-semibold">
              Share this insight or contact Abacus for turnkey consultation.
            </span>
            <Link
              to="/contact"
              className="bg-[#002b5c] text-white px-6 py-3 rounded-xs font-heading font-bold text-xs uppercase tracking-wider hover:bg-[#ffcc29] hover:text-[#002b5c] transition-all"
            >
              Start Project Inquiry
            </Link>
          </div>
        </div>
      </article>

      {/* Prev / Next Navigation */}
      <section className="bg-[#002b5c]/5 border-t border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {prevArticle ? (
            <button
              onClick={() => navigate(`/insights/${prevArticle.slug}`)}
              className="text-left group bg-white border border-gray-200 rounded-xs p-5 hover:border-[#002b5c] transition-all card-lift"
            >
              <span className="flex items-center gap-1 text-xs font-heading font-bold text-gray-400 uppercase tracking-wider mb-2">
                <ArrowLeft className="w-3.5 h-3.5" />
                Previous
              </span>
              <span className="font-heading text-sm font-bold text-[#002b5c] group-hover:text-[#ffcc29] transition-colors line-clamp-2 block">
                {prevArticle.title}
              </span>
            </button>
          ) : (
            <div />
          )}
          {nextArticle ? (
            <button
              onClick={() => navigate(`/insights/${nextArticle.slug}`)}
              className="text-right group bg-white border border-gray-200 rounded-xs p-5 hover:border-[#002b5c] transition-all card-lift sm:text-right"
            >
              <span className="flex items-center justify-end gap-1 text-xs font-heading font-bold text-gray-400 uppercase tracking-wider mb-2">
                Next
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
              <span className="font-heading text-sm font-bold text-[#002b5c] group-hover:text-[#ffcc29] transition-colors line-clamp-2 block">
                {nextArticle.title}
              </span>
            </button>
          ) : (
            <div />
          )}
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-12">
            <h3 className="font-heading text-2xl font-extrabold text-[#002b5c] uppercase tracking-tight mb-8">
              Related Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/insights/${rel.slug}`}
                  className="bg-white border border-gray-200 rounded-xs overflow-hidden flex flex-col shadow-xs card-lift group"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#002b5c]">
                    <img
                      src={rel.image}
                      alt={rel.imageAlt || rel.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-[#002b5c]/90 text-[#ffcc29] font-heading font-bold text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-xs backdrop-blur-sm">
                      {rel.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {rel.date}
                    </div>
                    <h4 className="font-heading text-base font-bold text-[#002b5c] mb-2 leading-snug line-clamp-2 group-hover:text-[#ffcc29] transition-colors">
                      {rel.title}
                    </h4>
                    <p className="text-gray-600 text-xs line-clamp-2">
                      {rel.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
